/**
 * CardGrid — Responsive grid container for TableCard / SSEProgressCard.
 *
 * Story 1.4 AC#3, AC#7, AC#8.
 *
 * 3-col at ≥1280px, 4-col at ≥1536px or when item count > 20.
 * Pagination at 20+ items.
 */

import { Children, useState, useMemo, type ReactNode } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CardGridProps {
  children: ReactNode[];
  /** Override: force compact (4-col) layout */
  forceCompact?: boolean;
  /** Items per page (default 20) */
  pageSize?: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CardGrid({
  children,
  forceCompact = false,
  pageSize = 20,
}: CardGridProps) {
  const [currentPage, setCurrentPage] = useState(1);
  // April 2026 fix: callers do
  //   <CardGrid>
  //     {arrayA.map(...)}
  //     {arrayB.map(...)}
  //     {arrayC.map(...)}
  //   </CardGrid>
  // which JSX delivers as children=[arrayA, arrayB, arrayC] — three
  // top-level entries, each itself an array. Without flattening,
  // ``children.length`` reports 3 instead of (Na + Nb + Nc), so we
  // produced 3 listitem cells with arrayC's many cards stacked into
  // one of them.  React.Children.toArray flattens fragments + nested
  // arrays into a single flat array of element children, which is
  // what we want to wrap into per-card listitem cells.
  const flatChildren = useMemo(
    () => Children.toArray(children).filter(Boolean) as ReactNode[],
    [children],
  );
  const totalItems = flatChildren.length;
  const isAtScale = totalItems > 20 || forceCompact;
  const totalPages = isAtScale ? Math.ceil(totalItems / pageSize) : 1;

  const visibleChildren = useMemo(() => {
    if (!isAtScale) return flatChildren;
    const start = (currentPage - 1) * pageSize;
    return flatChildren.slice(start, start + pageSize);
  }, [flatChildren, isAtScale, currentPage, pageSize]);

  // Reset page if items shrink
  if (currentPage > totalPages && totalPages > 0) {
    setCurrentPage(totalPages);
  }

  return (
    <div className="w-full" style={{ width: "100%", minWidth: 0 }}>
      {/*
        Grid — container-aware ``auto-fit`` layout, plus Tailwind viewport
        classes AND a final-resort inline media-query fallback so the
        cards multi-column under every condition we've seen reported.

        auto-fit is preferred over auto-fill because it COLLAPSES empty
        tracks at the end — fewer "ghost" columns when the row doesn't
        completely fill. Min-card width drops to 240px on the normal
        density variant so we still get 2 columns on a 540px-wide
        container (covers narrow browser windows + 1.5x zoom).

        Reproduction notes (April 2026):
          - Smoke (1280×720 Chromium) renders 3 cards per row reliably.
          - Some users on >1.25× browser zoom were seeing a single column
            because the container shrank below 2 × minWidth — fixed by
            the lowered minWidth + media-query fallback below.
      */}
      <div
        className={`ztd-card-grid grid gap-5 w-full ${
          forceCompact
            ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
            : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        }`}
        style={{
          display: "grid",
          width: "100%",
          minWidth: 0,
          gridTemplateColumns: forceCompact
            ? "repeat(auto-fit, minmax(220px, 1fr))"
            : "repeat(auto-fit, minmax(240px, 1fr))",
        }}
        role="list"
      >
        {visibleChildren.map((child, i) => (
          <div
            key={i}
            role="listitem"
            className="min-w-0"
            style={{ minWidth: 0 }}
          >
            {child}
          </div>
        ))}
      </div>

      {/* Pagination */}
      {isAtScale && totalPages > 1 && (
        <nav
          aria-label="Table grid pagination"
          className="flex items-center justify-center gap-2 mt-6"
        >
          <span className="text-caption text-[var(--muted-foreground)] mr-4">
            Showing {(currentPage - 1) * pageSize + 1}–
            {Math.min(currentPage * pageSize, totalItems)} of {totalItems}
          </span>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              aria-current={page === currentPage ? "page" : undefined}
              className={`
                w-8 h-8 rounded-md text-caption font-medium transition-colors
                ${
                  page === currentPage
                    ? "bg-[var(--readiness-excellent)] text-white"
                    : "text-[var(--muted-foreground)] hover:bg-white/10"
                }
              `}
            >
              {page}
            </button>
          ))}
        </nav>
      )}
    </div>
  );
}
